/**
 * **URL: none of its own.** A pathless layout route — the `_` prefix means it adds no
 * segment — wrapping every URL in the app: `/` and `/g/$gardenToken/*` alike. It renders
 * for all of them, which is why nothing here may assume a garden is in the URL.
 *
 * This is the hub, and it owns four things:
 *
 * 1. **Garden resolution.** Route token → `currentGarden`, across both cases: one of the
 *    user's own gardens, or a public link resolved through `storeToken`. The answer is
 *    published on `GardenContext` rather than passed down, and `useCurrentGarden()` is how
 *    the rest of the app reads it. The terminal "link unavailable" state is here too,
 *    because only this file knows the handshake failed.
 * 2. **The app chrome.** `AppChrome` picks the top-bar or sidebar arrangement, and is
 *    handed the destinations to place. It renders here rather than in the garden route so
 *    that the tab bar and top bar could become one sidebar — `docs/responsive-layout.md`
 *    §4.2, and §2 for the resulting tree. The destinations are links, so the router is the
 *    navigation state and none of it is held here; what remains is `lastTab`, which is not
 *    navigation but a record for the next cold start at `/`.
 * 3. **Overlays.** The modal, lightbox, detail-stack and drodal providers, and every
 *    handler that opens one (sharing, editing, adding a garden, the account modal). The
 *    chrome's controls only report that they were clicked; the modals belong here.
 * 4. **App-wide behaviour**: the document title, and the Back blocker that dismisses the
 *    top overlay instead of navigating the page underneath it.
 *
 * Three things that are *not* here, and where they live instead:
 *
 * - **Which garden `/` opens** — `_app.index.tsx`, and only there. `currentGarden` below is
 *   the garden this URL *names*; it never guesses one for a URL that names none.
 * - **The `?v=` search schema and the entries subscription** — the garden route
 *   (`_app.g.$gardenToken/route.tsx`), because both are scoped to one garden.
 * - **Anything a tab shows** — the four route files under `_app.g.$gardenToken/`.
 */

import { Button, Tooltip } from '@mantine/core';
import { useDisclosure, useDocumentTitle } from '@mantine/hooks';
import { ModalsProvider } from '@mantine/modals';
import { createFileRoute, Outlet, useBlocker, useMatchRoute, useNavigate } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useEffect, useMemo, useState } from 'react';

import AccountModal from '../components/AccountModal';
import { AppChrome } from '../components/AppChrome';
import { FabSlotContext } from '../components/BottomBar';
import { DetailDrodalBody, DetailDrodalFooter, DetailDrodalHeader } from '../components/DetailDrodal';
import { DetailStackProvider } from '../components/DetailStack';
import { DrodalStackProvider } from '../components/DrodalStack';
import { FullPageMessage } from '../components/FullPageMessage';
import { openAddGardenModal, openEditGardenModal } from '../components/GardenDialog';
import { GardenNav } from '../components/GardenNav';
import { LightboxProvider } from '../components/Lightbox';
import ModalsDismissOnBack from '../components/ModalsDismissOnBack';
import { openPublicGardenDialog } from '../components/PublicGardenDialog';
import { openShareGardenDialog } from '../components/ShareGardenDialog';
import { GardenContext } from '../hooks/useCurrentGarden';
import { CurrentUserProvider } from '../hooks/useCurrentUser';
import useDismissOnBack, { dismissTopOverlay } from '../hooks/useDismissOnBack';
import { useGardenResolution } from '../hooks/useGardenResolution';
import { usePersistentUIState } from '../hooks/usePersistentUIState';
import { GARDEN_JOURNAL_TABS, GardenJournalTabs, LAST_TAB_KEY } from '../lib/gardenTabs';

import classes from './_app.module.css';

export const Route = createFileRoute('/_app')({
  component: function AuthenticatedApp() {

    const matchRoute = useMatchRoute();
    const convex = useConvex();
    const navigate = useNavigate();

    // Which garden this URL shows, and everything that follows from it. The root redirect to
    // a garden is deliberately not in here — it lives in `_app.index.tsx`, and doing it as an
    // effect at this level made it fire once and never re-evaluate on later arrivals.
    //
    // `userGardens` is what the chrome's menu lists: always the user's own, a public link
    // included, so on one the menu shows someone else's garden at the top and the visitor's
    // own below — which is how they get back to one. It reaches the chrome undefined-and-all
    // rather than coalesced to `[]`, because the chrome distinguishes a list that has not
    // loaded from one that loaded empty.
    const {
      userGardens,
      currentGarden,
      routeGardenToken,
      isPublicLink,
      switchGarden,
    } = useGardenResolution();

    // figure out what tab we're on, and keep track of the last tab.
    //
    // This is *not* the navigation state — the destinations are links and the router
    // answers which one is current (GardenNav). The only reader of `lastTab` is the `/`
    // redirect in `_app.index.tsx`, i.e. where a cold start lands when the URL names no
    // garden. It lives at this level because that redirect does, and because the write has
    // to happen on every tab arrival rather than only where a garden route is mounted.
    const extractTab = () => {
      // @ts-expect-error TS7053
      const matchTab = matchRoute({ to: "/g/$gardenToken", fuzzy: true })["**"] as GardenJournalTabs | undefined;
      if (matchTab && GARDEN_JOURNAL_TABS.includes(matchTab)) {
        return matchTab;
      }
      return undefined;
    }
    const urlTab = extractTab();
    const [lastTab, setLastTab] = usePersistentUIState<GardenJournalTabs>(
      LAST_TAB_KEY,
      "journal",
      GARDEN_JOURNAL_TABS
    );
    // remember the tab for the next cold start. this has to be an effect: writing
    // localStorage during render is a side effect that can double-fire under
    // concurrent rendering
    useEffect(() => {
      if (urlTab && urlTab !== lastTab) {
        setLastTab(urlTab);
      }
    }, [urlTab, lastTab, setLastTab]);

    const title = useMemo(() => {
      const base = currentGarden
        ? `Plantings: ${currentGarden.name}`
        : "Plantings: plant tracker and garden journal";
      return window.location.host.endsWith("plantings.app") ? base : `DEV ${base} DEV`;
    }, [currentGarden]);
    useDocumentTitle(title);

    const [
      accountModalOpened,
      { open: accountModalOpen, close: accountModalClose },
    ] = useDisclosure(false);

    // `BottomBar`'s trailing action slot, which each garden route's FloatingButton portals
    // itself into. It lives here rather than in `AppChrome` because the provider has to wrap
    // `.contentLayer` and the chrome is that element's sibling; this is the one level that
    // renders both.
    //
    // State rather than a `useRef`, and this is the whole reason the slot is a *callback*
    // ref: the chrome commits together with the routed panel, so a ref object would still
    // hold null when that panel first renders, and nothing would re-render to fix it.
    // Setting state schedules the second commit the button appears in — on a cold start
    // only, since the chrome does not remount across navigations.
    const [fabSlot, setFabSlot] = useState<HTMLDivElement | null>(null);

    // Back dismisses the top overlay instead of navigating the page underneath
    // it. Blocking rather than pushing an entry: an overlay owns no history, so
    // the detail stack's index accounting is untouched (see useDismissOnBack).
    // Only "BACK" — the detail stack closes itself with history.go, which arrives
    // as "GO", and pushes must not be blocked at all.
    // enableBeforeUnload: false, or every reload would prompt to leave, since
    // this blocker is registered for the lifetime of the app.
    useBlocker({
      shouldBlockFn: ({ action }) => action === "BACK" && dismissTopOverlay(),
      enableBeforeUnload: false,
    });
    useDismissOnBack(accountModalOpened, accountModalClose);

    // A settled absence with a token in the URL can only mean the token failed: the URL tried
    // to name a garden and there is none to show. Without this the page would wait forever on
    // a link that has already failed.
    const publicLinkUnavailable = !!routeGardenToken && currentGarden === null;
    if (publicLinkUnavailable) {
      return (
        <FullPageMessage
          title="Link unavailable"
          body="This garden link is invalid or sharing has been turned off."
          action={
            <Button variant="light" onClick={() => void navigate({ to: "/" })}>
              Go to my gardens
            </Button>
          }
        />
      );
    }

   

    return (
      // Tooltip.Group lives here rather than main.tsx so @floating-ui stays out
      // of the entry bundle; all tooltips render under this route
      <Tooltip.Group openDelay={1000} closeDelay={100}>
        <CurrentUserProvider>
          <GardenContext.Provider value={currentGarden}>
            <ModalsProvider>
              <ModalsDismissOnBack />
              <LightboxProvider>
                {/* the slots are wired here, not inside DetailStackProvider: the
                    detail views they render call useDetailStack, so the provider
                    importing them would be a cycle — see
                    components/DetailStack/ARCHITECTURE.md */}
                <DetailStackProvider
                  drodalHeader={DetailDrodalHeader}
                  drodalBody={DetailDrodalBody}
                  drodalFooter={DetailDrodalFooter}
                >
                  <DrodalStackProvider>
                    {/* outside `.contentLayer` below, so the chrome is not trapped in that stacking
                        context under the FAB — exactly where TopBar sat before the hoist.
                        `currentGarden` is not passed: the controls inside read it from
                        the GardenContext above, which holds the same value */}
                    <AppChrome
                      nav={routeGardenToken
                        ? <GardenNav gardenToken={routeGardenToken} />
                        : undefined}
                      slotRef={setFabSlot}
                      gardens={userGardens}
                      isPublicLink={isPublicLink}
                      onAccountClick={accountModalOpen}
                      onGardenShare={openShareGardenDialog}
                      // guarded the way `onGardenEdit` below is: the menu item that calls
                      // this is only rendered when there is a garden, so the check is for the
                      // type rather than for a reachable state
                      onSharingClick={openPublicGardenDialog}
                      onGardenEdit={() => {
                        if (currentGarden) {
                          openEditGardenModal(convex, currentGarden, () => {});
                        }
                      }}
                      onGardenSelected={switchGarden}
                      // the modals all belong to this route, this one included —
                      // the menu only says that the item was clicked
                      onAddGarden={() => openAddGardenModal(
                        convex,
                        async (garden) => switchGarden(garden)
                      )}
                    />

                    {/* the app's background, and the only thing that declares it. Fixed, so
                        it covers the viewport however far the document has scrolled — which
                        is why neither box below has to paint. First in DOM order is what puts
                        it under `.contentLayer`; see _app.module.css */}
                    <div className={classes.contentBackground} />

                    {/* the viewport box every routed page renders into: one viewport tall, so
                        a route returning a terminal screen has something to fill, and no
                        constraint on `.panel`, which stays heightless with the *document* the
                        scroller — docs/scrolling.md §1.

                        Wrapped in the slot provider so that a route's FloatingButton can
                        portal itself into the chrome's row above. The portal moves the DOM
                        and not the React tree, so the button's handler still closes over the
                        route's own hooks — see components/BottomBar/ARCHITECTURE.md */}
                    <FabSlotContext.Provider value={fabSlot}>
                      <div className={classes.contentLayer}>
                        <Outlet />
                      </div>
                    </FabSlotContext.Provider>

                    {/* the rounded frame drawn over the content's edges, at regular width
                        only. After `.contentLayer` in DOM order is what puts it over the
                        content and still under the chrome; see _app.module.css */}
                    {/* <div className={classes.contentFrame} /> */}
                    <AccountModal
                      opened={accountModalOpened}
                      onClose={accountModalClose}
                    />
                  </DrodalStackProvider>
                </DetailStackProvider>
              </LightboxProvider>
            </ModalsProvider>
          </GardenContext.Provider>
        </CurrentUserProvider>
      </Tooltip.Group>
    );
  },
})
